import { useState } from 'react';
import { CheckCircle2, Lock, FileText, CalendarDays } from 'lucide-react';
import { post, postForm } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { Card, Button, Badge, Input, Textarea, EmptyState } from '../../components/UI';
import { fmtTime } from '../../utils/format';

/**
 * Pieces shared by the attendee pages. These mirror the app's behaviour one for
 * one — only the layout is different.
 */

/** Reads GL|session|<sessionId>|<eventId>|<sig>. The server verifies the signature. */
export function readSessionCode(code) {
  const parts = String(code || '').trim().split('|');
  if (parts.length !== 5 || parts[0] !== 'GL' || parts[1] !== 'session') return null;
  return { sessionId: parts[2], eventId: parts[3] };
}

/** Shown when the organiser left a module unticked for this event. */
export function NotIncluded({ what, icon }) {
  return (
    <EmptyState
      message={`${what} is not included in this event`}
      hint="The organiser has not set this event up with it. If that seems wrong, speak to your event coordinator."
      icon={icon}
    />
  );
}

export const chipStyle = (on) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', borderRadius: 99, fontSize: 12.8, fontWeight: 600, cursor: 'pointer',
  border: `1px solid ${on ? 'var(--gold-400)' : 'var(--border)'}`,
  background: on ? 'var(--gold-400)' : 'var(--ink-900)',
  color: on ? 'var(--ink-900)' : 'var(--text-mid)',
});

const isBlank = (v) => (Array.isArray(v) ? v.length === 0 : !String(v ?? '').trim());

/** One answer control, chosen by the question's type. */
function Answer({ field: f, value, onChange, onFile, file }) {
  if (['select', 'radio'].includes(f.type)) {
    return (
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
        {(f.options || []).map((o) => (
          <button key={o} type="button" style={chipStyle(value === o)} onClick={() => onChange(o)}>{o}</button>
        ))}
      </div>
    );
  }
  if (f.type === 'checkbox' && f.options?.length) {
    const picked = Array.isArray(value) ? value : [];
    return (
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
        {f.options.map((o) => {
          const on = picked.includes(o);
          return (
            <button key={o} type="button" style={chipStyle(on)}
              onClick={() => onChange(on ? picked.filter((x) => x !== o) : [...picked, o])}>
              {on && <CheckCircle2 size={12} />}{o}
            </button>
          );
        })}
      </div>
    );
  }
  if (f.type === 'checkbox') {
    return (
      <button type="button" style={chipStyle(Boolean(value))} onClick={() => onChange(value ? '' : 'Yes')}>
        {value ? 'Yes' : 'Tap to confirm'}
      </button>
    );
  }
  if (f.type === 'file') {
    return (
      <label style={{
        display: 'flex', alignItems: 'center', gap: 9, padding: '10px 12px', cursor: 'pointer',
        border: `1px dashed ${file ? 'var(--gold-500)' : 'var(--border)'}`, borderRadius: 'var(--r)', fontSize: 13,
        color: file ? 'var(--gold-300)' : 'var(--text-mid)',
      }}>
        <input type="file" hidden accept="image/*,application/pdf" onChange={(e) => onFile(e.target.files?.[0] || null)} />
        <FileText size={14} /> {file ? file.name : 'Choose a file (image or PDF)'}
      </label>
    );
  }
  if (f.type === 'textarea') {
    return <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder || ''} />;
  }
  const type = ['email', 'tel', 'number', 'date', 'time'].includes(f.type) ? f.type : 'text';
  return <Input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder || ''} />;
}

/**
 * The card the organiser pinned to the home screen: a form to fill in, or a
 * day of the schedule to confirm.
 */
export function AlertCard({ alert, eventId, onChanged }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState(alert.responses || {});
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState(false);

  if (alert.kind === 'schedule') {
    return (
      <Card padding={20} style={{ borderColor: 'rgba(239,212,138,0.3)' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <CalendarDays size={20} color={alert.answered ? 'var(--ok)' : 'var(--gold-400)'} style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ minWidth: 0 }}>
            <p style={{ fontSize: 15, fontWeight: 600 }}>{alert.title}</p>
            {alert.message && <p style={{ fontSize: 13, color: 'var(--text-mid)', marginTop: 4, lineHeight: 1.6 }}>{alert.message}</p>}
          </div>
        </div>
        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {alert.sessions.map((s) => (
            <div key={s._id} style={{ paddingTop: 12, borderTop: '1px solid var(--ink-750)' }}>
              <p style={{ fontSize: 13.5, fontWeight: 600 }}>
                <span style={{ color: 'var(--gold-400)', marginRight: 8 }}>{fmtTime(s.startAt)}</span>{s.title}
              </p>
              {s.attendancePoll?.enabled && <SessionPoll session={s} eventId={eventId} onAnswered={onChanged} compact />}
            </div>
          ))}
        </div>
      </Card>
    );
  }

  const fields = alert.form?.fields || [];

  const submit = async () => {
    const missing = fields.filter((f) => f.required && (f.type === 'file' ? !files[f.key] : isBlank(answers[f.key])));
    if (missing.length) return toast.error(`Please answer: ${missing.map((f) => f.label).join(', ')}`);
    setBusy(true);
    try {
      const attached = Object.entries(files).filter(([, v]) => v);
      let res;
      if (attached.length) {
        const body = new FormData();
        body.append('responses', JSON.stringify(answers));
        attached.forEach(([k, v]) => body.append(k, v));
        res = await postForm(`/forms/${alert.form._id}/submit`, body);
      } else {
        res = await post(`/forms/${alert.form._id}/submit`, { responses: answers });
      }
      toast.success(res.message);
      setOpen(false);
      onChanged();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card padding={20} style={{ borderColor: alert.submitted ? 'rgba(74,222,128,0.28)' : 'rgba(239,212,138,0.3)' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {alert.submitted
          ? <CheckCircle2 size={20} color="var(--ok)" style={{ flexShrink: 0, marginTop: 2 }} />
          : <FileText size={20} color="var(--gold-400)" style={{ flexShrink: 0, marginTop: 2 }} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 15, fontWeight: 600 }}>{alert.title}</p>
          <p style={{ fontSize: 13, color: 'var(--text-mid)', marginTop: 4, lineHeight: 1.6 }}>
            {alert.submitted
              ? 'Submitted — thanks. We\'ll let you know if anything else is needed.'
              : alert.reopened
                ? 'This has been updated — please check your answers and send them again.'
                : alert.message || alert.form?.description || 'Please fill this in.'}
          </p>
        </div>
      </div>

      {!alert.submitted && open && (
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {fields.map((f) => (
            <div key={f.key}>
              <p style={{ fontSize: 12.8, color: 'var(--text-mid)', fontWeight: 500, marginBottom: 7 }}>
                {f.label}{f.required ? <span style={{ color: 'var(--gold-400)' }}> *</span> : null}
              </p>
              <Answer
                field={f} value={answers[f.key]} file={files[f.key]}
                onChange={(v) => setAnswers({ ...answers, [f.key]: v })}
                onFile={(file) => setFiles({ ...files, [f.key]: file })}
              />
            </div>
          ))}
        </div>
      )}

      {!alert.submitted && (
        <div style={{ marginTop: 16, display: 'flex', gap: 9, flexWrap: 'wrap' }}>
          {open ? (
            <>
              <Button onClick={submit} loading={busy}>Submit</Button>
              <Button variant="subtle" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => setOpen(true)}>
              {alert.reopened ? 'Review your answers' : 'Fill it in'}
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

/** The organiser's poll on one schedule row. An answer is final. */
export function SessionPoll({ session, eventId, onAnswered, compact = false }) {
  const poll = session.attendancePoll;
  const mine = session.myAnswer;
  const toast = useToast();
  const [choices, setChoices] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async (body) => {
    setBusy(true);
    try {
      const res = await post(`/events/${eventId}/sessions/${session._id}/rsvp`, {
        ...body, ...(poll.allowNote && note.trim() ? { note: note.trim() } : {}),
      });
      toast.success(res.message);
      onAnswered();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const box = {
    marginTop: compact ? 9 : 14, padding: 13, borderRadius: 'var(--r)',
    background: 'var(--ink-900)', border: '1px solid var(--border)',
  };

  if (mine) {
    const said = mine.choices?.length ? mine.choices.join(', ') : (mine.attending ? 'Yes' : 'No');
    return (
      <div style={{ ...box, display: 'flex', gap: 9, alignItems: 'flex-start', borderColor: 'rgba(74,222,128,0.25)' }}>
        <CheckCircle2 size={15} color="var(--ok)" style={{ flexShrink: 0, marginTop: 1 }} />
        <div>
          <p style={{ fontSize: 13, fontWeight: 500 }}>You answered: {said}</p>
          {mine.note && <p style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: 3 }}>{mine.note}</p>}
        </div>
      </div>
    );
  }

  if (!session.pollOpen) {
    return (
      <div style={{ ...box, display: 'flex', gap: 8, alignItems: 'center' }}>
        <Lock size={13} color="var(--text-lo)" />
        <p style={{ fontSize: 12.5, color: 'var(--text-lo)' }}>Responses for this one are closed.</p>
      </div>
    );
  }

  return (
    <div style={box}>
      <p style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 10 }}>{poll.question || 'Will you attend?'}</p>
      {poll.allowNote && (
        <Input value={note} onChange={(e) => setNote(e.target.value)}
          placeholder={poll.noteLabel || 'Add a note (optional)'} style={{ marginBottom: 10 }} />
      )}
      {poll.type === 'choice' ? (
        <>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            {(poll.options || []).map((o) => {
              const on = choices.includes(o);
              return (
                <button key={o} type="button" disabled={busy} style={chipStyle(on)}
                  onClick={() => setChoices(poll.multi ? (on ? choices.filter((x) => x !== o) : [...choices, o]) : [o])}>
                  {o}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 11, flexWrap: 'wrap' }}>
            <Button size="sm" loading={busy}
              onClick={() => (choices.length ? send({ choices }) : toast.error('Choose an answer'))}>
              Submit answer
            </Button>
            <Badge tone="neutral">{poll.multi ? 'Pick any that apply · ' : ''}Final once sent</Badge>
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Button variant="outline" loading={busy} onClick={() => send({ attending: true })}>Yes</Button>
            <Button variant="subtle" disabled={busy} onClick={() => send({ attending: false })}>No</Button>
          </div>
          <p style={{ fontSize: 11.5, color: 'var(--text-lo)', marginTop: 8 }}>Your answer cannot be changed once sent.</p>
        </>
      )}
    </div>
  );
}
